import * as React from "react";
import { useToasts } from "react-toast-notifications";
import { Dialog } from "../../bits/Dialog/Dialog";
import { TextArea } from "../../bits/TextArea/TextArea";
import { Select } from "../../bits/Select/Select";
import { Input } from "../../bits/Input/Input";
import store from "../../../../store";
import {
    ClinicianOfficeWindowRequests,
    DateFormats,
    HeldOfficeProps,
    IClinician,
    IOfficeWindow,
    IOfficeWindowSchedule,
    OfficeRequestStatus,
    SignatureData,
} from "intake_shared/src/types";
import moment from "moment";
import { v4 as uuidv4 } from "uuid";
import {
    findAvailableTimeSlotsClinicianCanBook,
    getStringFromScheduleDate,
    getTimesUsedByClinicianForTheSelectedDay,
    getTimesUsedByOtherClincians,
    hasConflictingAvailability,
    hasConflictingAvailabilityWithCliniciansRemoteSchedules,
    sortStringsWithNumberEndings,
} from "../../../../utils/utils";
import { AclContext } from "../../../../contexts/AclContext";
import { CacheContext } from "../../../../contexts/CacheContext";
import { getIntakeEmailSignature } from "../../../../utils/intakeEmailSignature";
import { defaultOffices } from "../../../../store/cache/location";
import { uniq } from "lodash";
import styles from "./ApproveWithChangesDialog.module.scss";
import { cn } from "../../../helpers";

export interface RequestToPartiallyApproveProps {
    clinician_name: string;
    start_date: Date;
    end_date: Date;
    location: string;
    office: string;
    id: string;
    requester: string;
}

export interface IDraftApproveWithChangesRequest {
    from: string;
    to: string;
    reason: string;
    office: string;
}

interface ApproveWithChangesDialogProps {
    approveWithChangesRequest: boolean;
    setApproveWithChangesRequest: (value: React.SetStateAction<boolean>) => void;
    requestToApproveWithChanges: RequestToPartiallyApproveProps;
    row: ClinicianOfficeWindowRequests;
    setRefreshKey: (value: React.SetStateAction<number>) => void;
    setDraftApproveWithChanges: (
        value: React.SetStateAction<IDraftApproveWithChangesRequest>
    ) => void;
    draftApproveWithChanges: IDraftApproveWithChangesRequest;
    clinicianToApproveData: IClinician;
    heldWindows: HeldOfficeProps[];
}

export function ApproveWithChangesDialog({
    approveWithChangesRequest,
    setApproveWithChangesRequest,
    requestToApproveWithChanges,
    row,
    setRefreshKey,
    setDraftApproveWithChanges,
    draftApproveWithChanges,
    clinicianToApproveData,
    heldWindows,
}: ApproveWithChangesDialogProps) {
    const { offices: availableOffices = defaultOffices } = React.useContext(
        CacheContext
    ) || { offices: defaultOffices };
    const loggedInUserEmail = React.useContext(AclContext)?.authUser.user?.email;
    const { addToast } = useToasts();
    const olc = React.useContext(CacheContext);
    const intakeSignature = getIntakeEmailSignature(olc);
    const [loading, setLoading] = React.useState(false);
    const [existingSchedules, setExistingSchedules] = React.useState(
        [] as IOfficeWindowSchedule[]
    );
    const [clinicianList, setClinicianList] = React.useState<IClinician[]>([]);
    const [slotsAvailableForClinician, setSlotsAvailableForClinician] =
        React.useState<string[]>();
    const [startDateToUse, setStartDateToUse] = React.useState(moment());
    const [endDateToUse, setEndDateToUse] = React.useState(moment());

    const fetchCliniciansByLocation = React.useCallback(async (location: string) => {
        setClinicianList((await store.fetchAllCliniciansByLocation(location)) || []);
    }, []);

    React.useEffect(() => {
        fetchCliniciansByLocation(requestToApproveWithChanges.location);
    }, [requestToApproveWithChanges.location, fetchCliniciansByLocation]);

    React.useEffect(() => {
        let schedules: IOfficeWindowSchedule[] = [];
        clinicianList.forEach((c) => {
            schedules = schedules.concat(
                c.office_windows
                    .filter(
                        (w) => w.location === requestToApproveWithChanges.location
                    )
                    .map((w) => {
                        return {
                            clinicianId: c.id,
                            clinicianEmail: c.email,
                            id: w.id,
                            assigned_to: `${c.firstname} ${c.lastname}`,
                            start: w.start,
                            ending_date: w.ending_date,
                            end: w.end,
                            office: w.office,
                            location: requestToApproveWithChanges.location,
                        };
                    })
            );
        });

        setExistingSchedules([...schedules, ...heldWindows]);
    }, [clinicianList, requestToApproveWithChanges.location, heldWindows]);

    React.useEffect(() => {
        const timeRegex = /(\d{1,2}):(\d{2})\s*([ap]m)?/i;
        const match = draftApproveWithChanges.to.match(timeRegex);

        const startDate = moment(row?.start_date)
            .clone()
            .hour(parseInt(draftApproveWithChanges.from.split(":")[0]))
            .minute(parseInt(draftApproveWithChanges.from.split(":")[1]))
            .second(0)
            .milliseconds(0);
        setStartDateToUse(startDate);

        let endDate;
        if (match) {
            const hours = parseInt(match[1]);
            const minutes = parseInt(match[2]);
            const period = match[3] ? match[3].toLowerCase() : "am";
            const adjustedHours = period === "pm" ? hours + 12 : hours;

            endDate = moment(row?.start_date)
                .clone()
                .hour(adjustedHours)
                .minute(minutes)
                .second(0)
                .milliseconds(0);
            setEndDateToUse(endDate);
        }
    }, [draftApproveWithChanges?.to, draftApproveWithChanges?.from, row]);

    React.useEffect(() => {
        // get all the times that are not available into one array
        const getAllUnAvailableTimes = () => {
            const timesUsedByClinician = getTimesUsedByClinicianForTheSelectedDay(
                clinicianToApproveData?.office_windows,
                moment(row?.start_date).format(DateFormats.DAY)
            );
            const timesUsedByOtherClinicians = getTimesUsedByOtherClincians(
                existingSchedules,
                row?.location,
                draftApproveWithChanges.office,
                moment(row?.start_date).format(DateFormats.DAY)
            );

            const allUnAvailableTimes = [
                ...timesUsedByClinician,
                ...timesUsedByOtherClinicians,
            ];
            return allUnAvailableTimes;
        };
        // this is the time slots that are occupied for that day
        const occupiedTimeSlots = getAllUnAvailableTimes();

        const slotsAvailable =
            findAvailableTimeSlotsClinicianCanBook(occupiedTimeSlots);
        setSlotsAvailableForClinician(slotsAvailable);
    }, [
        clinicianToApproveData,
        draftApproveWithChanges.office,
        existingSchedules,
        row,
    ]);

    const handleApproveWithChanges = async () => {
        const hasConflict = hasConflictingAvailability(
            existingSchedules,
            startDateToUse.toISOString(),
            endDateToUse.toISOString(),
            draftApproveWithChanges.office,
            requestToApproveWithChanges.location,
            loggedInUserEmail as string
        );
        const request = await store.getClinicianByEmail(
            requestToApproveWithChanges.requester
        );

        const clincianToApproveRemoteWindows = request?.office_windows.filter(
            (window) => window.location === "Remote"
        );
        const requestHasConflictWithClinicianRemoteSchedules =
            await hasConflictingAvailabilityWithCliniciansRemoteSchedules(
                clincianToApproveRemoteWindows as IOfficeWindow[],
                startDateToUse.toISOString(),
                endDateToUse.toISOString()
            );

        const bookingData = {
            id: uuidv4(),
            clinicianId: row.requester,
            office: draftApproveWithChanges.office,
            location: row.location,
            start: getStringFromScheduleDate(startDateToUse.toDate()),
            end: getStringFromScheduleDate(endDateToUse.toDate()),
            ending_date:
                row.ending_date && getStringFromScheduleDate(row.ending_date),
            status: OfficeRequestStatus.APPROVED_WITH_CHANGES,
            assigned_to: row.clinician_name as string,
            available: false,
            office_window_request_id: row.office_window_request_id as string,
            approve_with_changes_reason: draftApproveWithChanges.reason,
            office_manager: loggedInUserEmail as string,
            category: row.category,
            intakeSignature: intakeSignature || ({} as SignatureData),
        };

        if (hasConflict || requestHasConflictWithClinicianRemoteSchedules) {
            addToast(
                [
                    `Conflicting schedule with ${
                        hasConflict?.clinicianId ||
                        "this clinician's remote schedule"
                    },`,
                    `${
                        hasConflict?.location ||
                        requestHasConflictWithClinicianRemoteSchedules?.location
                    }, ${
                        hasConflict?.office ||
                        requestHasConflictWithClinicianRemoteSchedules?.office
                    }`,
                    `@ ${moment(
                        getStringFromScheduleDate(
                            hasConflict?.start ||
                                requestHasConflictWithClinicianRemoteSchedules?.start
                        )
                    ).format(DateFormats.DAY_HOUR_FORMAT)} - ${moment(
                        getStringFromScheduleDate(
                            hasConflict?.end ||
                                requestHasConflictWithClinicianRemoteSchedules?.end
                        )
                    ).format(DateFormats.DAY_HOUR_FORMAT)}`,
                ].join(" "),
                { appearance: "error", autoDismiss: false }
            );
            return;
        }

        if (!hasConflict || !requestHasConflictWithClinicianRemoteSchedules) {
            setLoading(true);
            try {
                await store.approveClinicianOfficeWindow(bookingData);
                setRefreshKey((prevKey) => prevKey + 1);
                setApproveWithChangesRequest(false);
                setDraftApproveWithChanges({
                    ...draftApproveWithChanges,
                    reason: "",
                });
                addToast("Office window saved", {
                    appearance: "success",
                });
            } catch (error) {
                console.error("Error approving with changes:", error);
                addToast("Failed to approve with changes", { appearance: "error" });
            } finally {
                setLoading(false);
            }
        }
    };

    const handleClose = () => {
        setApproveWithChangesRequest(false);
    };

    const getTitle = () => {
        return `${requestToApproveWithChanges.clinician_name} - ${
            requestToApproveWithChanges.location
        } (${requestToApproveWithChanges.office}) ${moment(
            requestToApproveWithChanges.start_date
        ).format(DateFormats.DAY)} - ${moment(
            requestToApproveWithChanges.start_date
        ).format(DateFormats.HOUR_MIN_FORMAT)} - ${moment(
            requestToApproveWithChanges.end_date
        ).format(DateFormats.HOUR_MIN_FORMAT)}`;
    };

    const officeOptions = sortStringsWithNumberEndings(
        uniq(availableOffices[requestToApproveWithChanges.location] || [])
    ).map((office) => ({
        value: office,
        label: office,
    }));

    return (
        <>
            {row && (
                <Dialog
                    open={approveWithChangesRequest}
                    onClose={handleClose}
                    title={getTitle()}
                    onCancel={handleClose}
                    onSave={handleApproveWithChanges}
                    isSubmitBtnDisabled={
                        draftApproveWithChanges.reason === "" ||
                        loading ||
                        !moment(startDateToUse.toDate()).isBefore(
                            moment(endDateToUse.toDate())
                        )
                    }
                    isCancelBtnDisabled={loading}
                    saveText={loading ? "Approving..." : "Approve Request"}
                >
                    <div className={cn(styles.approveWithChangesForm, "v2-form")}>
                        {clinicianToApproveData &&
                            slotsAvailableForClinician &&
                            slotsAvailableForClinician?.length > 0 && (
                                <div className={styles.availableTime}>
                                    Available time on{" "}
                                    {moment(row?.start_date).format(DateFormats.DAY)}{" "}
                                    you can book for this clinician includes:
                                    <p className={styles.availableTimeSlots}>
                                        {slotsAvailableForClinician?.join(", ")}
                                    </p>
                                </div>
                            )}

                        <div>
                            <Select
                                label="Office"
                                options={officeOptions}
                                value={officeOptions.find(
                                    (opt) =>
                                        opt.value === draftApproveWithChanges.office
                                )}
                                onChange={(option: any) => {
                                    setDraftApproveWithChanges({
                                        ...draftApproveWithChanges,
                                        office: option?.value || "",
                                    });
                                }}
                                isRequired
                                placeholder="Select an office"
                                isLongListInDialog
                            />
                        </div>

                        <div className="gridTwo">
                            <Input
                                label="From"
                                type="time"
                                min="07:00"
                                max="22:00"
                                step={900}
                                value={moment(
                                    draftApproveWithChanges.from,
                                    "HH:mm"
                                ).format("HH:mm")}
                                onChange={(e) => {
                                    setDraftApproveWithChanges({
                                        ...draftApproveWithChanges,
                                        from: e.target.value,
                                    });
                                }}
                                isRequired
                            />
                            <Input
                                label="To"
                                type="time"
                                min="07:00"
                                max="22:00"
                                step={900}
                                value={moment(
                                    draftApproveWithChanges.to,
                                    "HH:mm"
                                ).format("HH:mm")}
                                onChange={(e) => {
                                    setDraftApproveWithChanges({
                                        ...draftApproveWithChanges,
                                        to: e.target.value,
                                    });
                                }}
                                isRequired
                            />
                        </div>

                        <TextArea
                            label="Provide reasons for the time changes"
                            value={draftApproveWithChanges.reason}
                            onChange={(e) => {
                                setDraftApproveWithChanges({
                                    ...draftApproveWithChanges,
                                    reason: e.target.value,
                                });
                            }}
                            isRequired
                            placeholder="Enter reason for changes..."
                            className={styles.reasonForChanges}
                        />
                    </div>
                </Dialog>
            )}
        </>
    );
}
